import { Link } from "react-router-dom";
import { Table2, AlertTriangle } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function DatabaseOverview({ db, mainId, onMainChange }) {
  const { isAdmin } = useAuth();
  const isMain = mainId === db.id;
  const toggleMain = async () => {
    try {
      const { data } = await api.put("/app-settings", { main_db_id: isMain ? null : db.id });
      onMainChange(data.main_db_id);
      toast.success(isMain ? "Main database cleared" : `${db.name} is now your main database`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const total = db.tables.reduce((s, t) => s + t.row_count, 0);
  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">{db.filename}</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2" data-testid="db-title">{db.name}</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="db-summary">
            {db.tables.length} tables · {total.toLocaleString()} records · imported {new Date(db.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
        {isMain && !isAdmin && <span className="text-xs text-amber-400 self-center">Main database</span>}
        {isAdmin && (
          <Button variant="outline" onClick={toggleMain} data-testid="set-main-db-button" className="hover:bg-secondary hover:text-foreground">
            <Star size={16} className={isMain ? "text-amber-400 fill-amber-400" : ""} /> {isMain ? "Main database" : "Set as main database"}
          </Button>
        )}
        <ExportMenu path={`/databases/${db.id}/export`} formats={["accdb", "wdb", "xlsx", "json"]} name={db.name} testId="export-db-button" label="Download all tables" />
        </div>
      </div>
      <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="table-cards">
        {db.tables.map((t, i) => (
          <Link key={t.name} to={`/db/${db.id}/${encodeURIComponent(t.name)}`} data-testid={`table-card-${t.name.replace(/\s+/g, "-")}`}
            className="group border rounded-lg p-5 bg-card hover:border-primary transition-colors fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-center justify-between">
              <Table2 size={18} className="text-primary" />
              <span className="font-mono text-xs text-muted-foreground">{t.row_count} rows</span>
            </div>
            <p className="mt-4 font-display text-lg font-bold group-hover:text-primary transition-colors">{t.name}</p>
            <p className="text-xs text-muted-foreground mt-1 truncate">{t.columns.map((c) => c.name).join(", ") || "—"}</p>
            {t.error && <p className="mt-2 text-xs text-amber-400 flex items-center gap-1"><AlertTriangle size={12} /> {t.error}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}
